import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ButtonBlockPrimary, LayoutLearning } from '../../../components';
import {
	getPostTestForVideo,
	getPostTestResult,
	submitPostTestAnswers,
} from '../../../service/postTestService';
import { TPostTestForLearning, TPostTestResult } from '../../../type/postTest';

type Mode = 'loading' | 'result' | 'quiz';

const LearningPostTest = () => {
	const { learningAssetsVideoUniqueId } = useParams();

	const [mode, setMode] = useState<Mode>('loading');
	const [postTestData, setPostTestData] =
		useState<TPostTestForLearning | null>(null);
	const [result, setResult] = useState<TPostTestResult | null>(null);
	const [answers, setAnswers] = useState<Record<string, string>>({});
	const [submitting, setSubmitting] = useState(false);

	const loadQuestions = async () => {
		if (!learningAssetsVideoUniqueId) {
			return;
		}

		const response = await getPostTestForVideo(learningAssetsVideoUniqueId);
		if (response?.error) {
			toast.error(response.message);
			return;
		}

		setPostTestData(response.data);
		setAnswers({});
		setMode('quiz');
	};

	const loadStatus = async () => {
		if (!learningAssetsVideoUniqueId) {
			return;
		}

		setMode('loading');
		const response = await getPostTestResult(learningAssetsVideoUniqueId);
		if (response?.error) {
			toast.error(response.message);
			return;
		}

		if (response.data) {
			setResult(response.data);
			setMode('result');
			return;
		}

		await loadQuestions();
	};

	useEffect(() => {
		loadStatus();
	}, [learningAssetsVideoUniqueId]);

	const selectAnswer = (questionUniqueId: string, choiceUniqueId: string) => {
		setAnswers((prev) => ({ ...prev, [questionUniqueId]: choiceUniqueId }));
	};

	const handleSubmit = async () => {
		if (!postTestData) {
			return;
		}

		const totalQuestions = postTestData.questions.length;
		const answeredCount = Object.keys(answers).length;
		if (answeredCount < totalQuestions) {
			toast.error('Harap jawab semua pertanyaan terlebih dahulu.');
			return;
		}

		const payload = {
			learningAssetsVideoUniqueId: postTestData.learningAssetsVideoUniqueId,
			answers: Object.entries(answers).map(
				([questionUniqueId, choiceUniqueId]) => ({
					questionUniqueId,
					choiceUniqueId,
				})
			),
		};

		setSubmitting(true);
		const response = await submitPostTestAnswers(payload);
		setSubmitting(false);

		if (response?.error) {
			toast.error(response.message);
			return;
		}

		setResult(response.data);
		setMode('result');
	};

	return (
		<LayoutLearning btnBack={true}>
			<div className="mx-4 mt-16">
				{mode === 'loading' && (
					<p className="text-[13px] text-center text-gray-500">Memuat...</p>
				)}

				{mode === 'result' && result && (
					<div className="bg-gray-100 p-4 rounded-sm text-center">
						<p className="text-[13px]">
							Anda sudah pernah mengerjakan post test ini
						</p>
						<p className="text-[32px] font-bold text-sky-600 mt-2">
							{Math.round(result.score)}%
						</p>
						<p className="text-[12px] text-gray-500 mb-4">
							{result.correctCount} dari {result.totalQuestions} jawaban benar
						</p>
						<ButtonBlockPrimary
							label="Kerjakan Ulang"
							onClick={loadQuestions}
						/>
					</div>
				)}

				{mode === 'quiz' && postTestData && (
					<>
						<h1 className="text-[14px] font-bold mb-3">
							{postTestData.postTestName}
						</h1>
						<div className="flex flex-col gap-3">
							{postTestData.questions.map((question, qIndex) => (
								<div
									key={question.uniqueId}
									className="bg-gray-100 p-3 rounded-sm"
								>
									<p className="font-semibold text-[13px] mb-2">
										{qIndex + 1}. {question.questions}
									</p>
									<div className="flex flex-col gap-2">
										{question.choices.map((choice) => (
											<label
												key={choice.uniqueId}
												className="flex items-center gap-2 text-[13px]"
											>
												<input
													type="radio"
													name={question.uniqueId}
													checked={
														answers[question.uniqueId] === choice.uniqueId
													}
													onChange={() =>
														selectAnswer(question.uniqueId, choice.uniqueId)
													}
												/>
												{choice.choice}
											</label>
										))}
									</div>
								</div>
							))}
						</div>
						<div className="mt-4">
							<ButtonBlockPrimary
								label={submitting ? 'Mengirim...' : 'Kirim Jawaban'}
								isDisable={submitting}
								onClick={handleSubmit}
							/>
						</div>
					</>
				)}
			</div>
		</LayoutLearning>
	);
};

export default LearningPostTest;
